import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { z } from 'zod';
import { setCollaboratorCallEnded } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import type { EmergencyCallsState } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';

type RootState = {
  emergencyCalls: EmergencyCallsState;
};

const CALL_DATA_EVENT_TYPE = 'call_data.event';
const CALL_ENDED_EVENT_TYPE = 'call_ended';

const callEndedWebSocketMessageSchema = z.object({
  type: z.literal(CALL_DATA_EVENT_TYPE),
  topic: z.string(),
  body: z.object({
    event: z.object({
      event_name: z.literal('call_status'),
      event_type: z.literal(CALL_ENDED_EVENT_TYPE),
    }),
  }),
});

/**
 * Hook that handles call_ended websocket events for collaborator calls.
 * Updates the collaborator.callEnded status in Redux when a call ends.
 *
 * Note: Endpoint-loaded collaborator data is handled in getRTTCollaboratorData.fulfilled.
 */
const useHandleCollaboratorCallEnded = () => {
  const StreamWS = window?.__RSOS?.streamWS?.instance;
  const dispatch = useDispatch();
  const callQueue = useSelector(
    (state: RootState) => state.emergencyCalls.callQueue,
  );

  const handleCallEndedEvent = useCallback(
    (msg: Record<string, unknown>) => {
      const parsed = callEndedWebSocketMessageSchema.safeParse(msg);
      if (!parsed.success) return;

      const { topic } = parsed.data;
      const callerId = topic.split('tel://')[1];
      if (!callerId) {
        // eslint-disable-next-line no-console -- Log for sentry
        console.error('No callerId found in topic:', topic);
        return;
      }

      const call = callQueue[callerId];

      // Only mark collaborator calls as ended
      if (call?.collaborator?.type) {
        dispatch(
          setCollaboratorCallEnded({ callerID: callerId, callEnded: true }),
        );
      }
    },
    [dispatch, callQueue],
  );

  useEffect(() => {
    if (StreamWS) {
      StreamWS.addOnMessageCallback({
        handleCollaboratorCallEnded: handleCallEndedEvent,
      });

      return () => {
        StreamWS.removeOnMessageCallback('handleCollaboratorCallEnded');
      };
    }
  }, [StreamWS, handleCallEndedEvent]);

  return null;
};

export default useHandleCollaboratorCallEnded;
